/**
 * Offline Database Utility for Main Application
 * Handles IndexedDB operations for offline functionality
 */

import { openDB } from 'idb';
import { DB_CONFIG } from '../config/db.config';

class OfflineDB {
  constructor() {
    this.db = null;
    this.initPromise = null;
  }

  // Helper function to determine if a store should be skipped
  shouldSkipStore(storeName) {
    // Allow ticket_users table
    if (storeName === 'ticket_users') {
      return false;
    }
    
    // Skip sec_ and whatsapp_ tables
    if (storeName.startsWith('sec_') || storeName.startsWith('whatsapp_')) {
      return true;
    }
    
    return false;
  }

  // Helper function to validate response data
  isValidResponseData(data) {
    if (!data) return false;
    if (typeof data !== 'object') return false;
    if (Array.isArray(data)) return data.length > 0;
    return Object.keys(data).length > 0;
  }

  async init() {
    try {
      this.db = await openDB(DB_CONFIG.NAME, DB_CONFIG.VERSION, {
        upgrade(db) {
          // Create all stores using the shared configuration
          Object.entries(DB_CONFIG.STORE_CONFIGS).forEach(([name, options]) => {
            if (!db.objectStoreNames.contains(name)) {
              console.log(`Creating store: ${name}`);
              const store = db.createObjectStore(name, options);
              
              // Add indexes if defined for this store
              if (DB_CONFIG.INDEXES && DB_CONFIG.INDEXES[name]) {
                DB_CONFIG.INDEXES[name].forEach(index => {
                  store.createIndex(index.name, index.keyPath, { unique: index.unique });
                });
              }
            }
          });
        }
      });
      return true;
    } catch (error) {
      console.error('Failed to initialize offline database:', error);
      return false;
    }
  }

  async store(storeName, data) {
    if (this.shouldSkipStore(storeName)) {
      console.log(`Skipping store operation for ${storeName}`);
      return false;
    }

    if (!this.isValidResponseData(data)) {
      console.log(`Skipping store operation - invalid data for ${storeName}`);
      return false;
    }

    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      
      // If data is an array, store each item
      if (Array.isArray(data)) {
        console.log(`Storing ${data.length} items in ${storeName}`);
        for (const item of data) {
          await store.put(item);
        }
      } else {
        await store.put(data);
      }
      
      await tx.complete;
      return true;
    } catch (error) {
      console.error(`Failed to store data in ${storeName}:`, error);
      return false;
    }
  }

  async get(storeName, key) {
    console.log('Getting data from store:', storeName, 'with key:', key);
    
    if (this.shouldSkipStore(storeName)) {
      console.log(`Skipping get operation for ${storeName}`);
      return null;
    }

    if (!this.db) {
      console.log('Database not initialized, initializing...');
      await this.init();
    }
    
    try {
      const tx = this.db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const result = await store.get(key);
      console.log('Database get result:', result);
      return result;
    } catch (error) {
      console.error(`Failed to get data from ${storeName}:`, error);
      return null;
    }
  }

  async getAll(storeName) {
    if (this.shouldSkipStore(storeName)) {
      console.log(`Skipping getAll operation for ${storeName}`);
      return [];
    }

    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      return await store.getAll();
    } catch (error) {
      console.error(`Failed to get all data from ${storeName}:`, error);
      return [];
    }
  }

  async delete(storeName, key) {
    if (this.shouldSkipStore(storeName)) {
      console.log(`Skipping delete operation for ${storeName}`);
      return false;
    }

    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      await store.delete(key);
      await tx.complete;
      return true;
    } catch (error) {
      console.error(`Failed to delete data from ${storeName}:`, error);
      return false;
    }
  }

  async clear(storeName) {
    if (this.shouldSkipStore(storeName)) {
      console.log(`Skipping clear operation for ${storeName}`);
      return false;
    }

    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      await store.clear();
      await tx.complete;
      return true;
    } catch (error) {
      console.error(`Failed to clear ${storeName}:`, error);
      return false;
    }
  }

  async query(storeName, indexName, value) {
    if (this.shouldSkipStore(storeName)) {
      console.log(`Skipping query operation for ${storeName}`);
      return [];
    }

    if (!this.db) await this.init();
    
    try {
      const tx = this.db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const index = store.index(indexName);
      return await index.getAll(value);
    } catch (error) {
      console.error(`Failed to query ${storeName} by ${indexName}:`, error);
      return [];
    }
  }

  async storeApiResponse(endpoint, response) {
    try {
      // Skip security and whatsapp tables
      if (endpoint.includes('/db/sec_') || endpoint.includes('/db/whatsapp_')) {
        return;
      }

      // Extract data array from response
      const dataArray = response.data;
      if (!dataArray || !Array.isArray(dataArray)) {
        console.log('No valid data array in response');
        return;
      }

      console.log(`Storing ${dataArray.length} items from response`);

      // Get store name from endpoint
      const storeName = endpoint.split('/')[2];
      if (!storeName || !DB_CONFIG.STORE_CONFIGS[storeName]) {
        console.warn('Invalid endpoint or store not configured:', endpoint);
        return;
      }

      // Store each item from the data array
      const tx = this.db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      
      for (const item of dataArray) {
        await store.put(item);
      }
      
      await tx.complete;
      console.log(`Successfully stored ${dataArray.length} items in ${storeName}`);
      return true;
    } catch (error) {
      console.error('Failed to store API response:', error);
      return false;
    }
  }

  async getCachedApiResponse(endpoint, requestData = null) {
    try {
      // Skip security and whatsapp tables
      if (endpoint.includes('/db/sec_') || endpoint.includes('/db/whatsapp_')) {
        return null;
      }

      const storeName = endpoint.split('/')[2];
      if (!storeName || !DB_CONFIG.STORE_CONFIGS[storeName]) {
        console.warn('Invalid store name or store not configured');
        return null;
      }

      // For filter endpoints, get all data and apply filters
      if (endpoint.endsWith('/filter')) {
        try {
          // Get all data from the store
          const allData = await this.getAll(storeName);
          console.log(`Retrieved ${allData.length} items from ${storeName}`);

          // If there's a filter request body, apply filters
          let filteredData = allData;
          if (requestData?.filters) {
            filteredData = allData.filter(item => {
              return Object.entries(requestData.filters).every(([key, value]) => {
                return item[key] === value;
              });
            });
            console.log(`Filtered to ${filteredData.length} items`);
          }

          // Return in the same format as the API
          return {
            status: 200,
            statusText: "OK",
            headers: {
              "content-type": "application/json"
            },
            data: filteredData
          };
        } catch (error) {
          console.error('Error handling filter request:', error);
          return {
            status: 500,
            statusText: "Error",
            data: []
          };
        }
      }

      // For non-filter endpoints, return single item
      const item = await this.get(storeName, endpoint);
      return {
        status: item ? 200 : 404,
        statusText: item ? "OK" : "Not Found",
        headers: {
          "content-type": "application/json"
        },
        data: item || null
      };
    } catch (error) {
      console.error('Failed to get cached API response:', error);
      return {
        status: 500,
        statusText: "Error",
        data: null
      };
    }
  }
}

// Create and export singleton instance
const offlineDB = new OfflineDB();
export default offlineDB; 